import {CLOSE_DATE, SECOND_OPEN_DATE, SECOND_CLOSE_DATE, TIME_ZONE} from '../utils/Constants'
import ExternalLink from '../misc/ExternalLink'
import UnsplashCredit from './UnsplashCredit'
import MailSupport from '../misc/MailSupport'
import ScoringTable from './ScoringTable'
import FaqListItem from './FaqListItem'
import React from 'react'

export default function Faq() {
  return (
    <div className="faq-container">
      <h1>Frequently Asked Questions</h1>
      <ol>
        <FaqListItem
          question="Any affiliation with the tournament?"
          answer="This website is not affiliated with FIFA in any way. It was created just for fun as a way to follow
          along with the tournament and compete with friends and colleagues."
        />
        <FaqListItem
          question="Who can create a bracket?"
          answer="Anyone! It's free and you don't need to create an account."
        />
        <FaqListItem
          question="Can I compete with my friends?"
          answer={'Yes! The best way to play with friends is to create a pool and invite ' +
            'everyone you\'d like to join. To create a pool, click on the "Pools" tab at the top of page, create a ' +
            'pool name and an entry password. Share that name and password with your friends, or the direct link ' +
            '(copied from the "Invite friends" button on your pool\'s leaderboard).'}
        />
        <FaqListItem
          question="What is the maximum number of people who can enter a pool?"
          answer="There is no limit to the number of people or entries in a pool, and all pools are free."
        />
        <FaqListItem
          question="When does the entry period end?"
          answer={'Brackets can be created up until the start of the Group Stage. All brackets must be submitted by ' +
            `${CLOSE_DATE.human} ${TIME_ZONE}.`}
        />
        <FaqListItem
          question="If my bracket is terrible, is there a second chance opportunity?"
          answer={`Yes! Second chance brackets open at the end of the Group Stage, ${SECOND_OPEN_DATE.human} 
          ${TIME_ZONE}, and close at the start of the Knockout Stage, ${SECOND_CLOSE_DATE.human} ${TIME_ZONE}. 
          It's a short window, so be sure to mark your calendar. At the end of the Group Stage, you have the option to
          create a new bracket with the knockout stage already filled out. These brackets are completely separate from the
          main brackets that were created prior to the Group Stage. They appear in a separate leaderboard either next to
          or under the main leaderboard depending on your device's screen size. The scoring for these brackets is
          different as well. Think of second chance brackets as a separate consolation competition.`}
        />
        <FaqListItem
          question="Who can create a second chance bracket?"
          answer="Anyone can create a second chance bracket, they do not need to already be in the pool. You can invite
          people to your pool using the same link or password as before."
        />
        <FaqListItem
          question="How does the scoring work?"
          answer={<>
            The scoring system is strict. A pick only counts if the team is in the correct slot in the bracket, not
            just if the team eventually reaches that round. That means getting the group winners and runners-up in the
            right order matters, because it determines the entire knockout path.
            <br /><br />
            This is even more important for advancing third-place teams. In 2026, the eight third-place teams can come
            from many different group combinations, and their round-of-32 placements depend on which groups advance.
            There are 495 possible eight-group combinations, so a team can qualify as a third-place team and still end
            up in a different bracket slot than you picked. See Annexe C in FIFA's official 2026 regulations
            {' '}<ExternalLink
              href="https://digitalhub.fifa.com/m/636f5c9c6f29771f/original/FWC2026_regulations_EN.pdf"
              text="here"
            />.
            <br /><br />
            Example: if you pick Brazil to win its group and win the tournament, but Brazil actually finishes second in
            the group and still wins the tournament, you would miss the early-round points tied to the wrong bracket
            path. You would still earn points in the later rounds once Brazil reaches the slot you picked correctly.
          </>}
        />
        <ScoringTable />
        <FaqListItem
          question="A team I picked advance to the knockout stage, why is it crossed out in later rounds?"
          answer={'Because the scoring is strict, advancing is not enough by itself. If you picked a team to win its ' +
            'group but it actually finished second, or if you placed an advancing third-place team into the wrong ' +
            'round-of-32 slot, then that pick is treated as incorrect for the rounds tied to that slot. You can still ' +
            'earn points later if that team reaches a later matchup in the exact position you picked correctly. For ' +
            'more detail, see "How does the scoring work?" above.'}
        />
        <FaqListItem
          question="When will the scores update?"
          answer="Scores recalculate at the end of every match. In the Group Stage, scores will begin to change after a
          team clinches their spot for the Knockout Stage. Typically that will happen after a team plays in their third
          Group Stage match, but can sometimes be sooner. In the Knockout Stage, scores update at the end of each match
          after a winner is determined."
        />
        <FaqListItem
          question="Why can't I see anyone else's bracket in my pool?"
          answer="You cannot see anyone else's bracket while brackets are being created before the tournament. You will
          be able to see everyone's bracket at the start of the Group Stage."
        />
        <FaqListItem
          question="Where do the rankings come from?"
          answer={<>{'The rankings you see when you create a bracket are the latest official world rankings. '}
            <ExternalLink text="Learn more." href="https://inside.fifa.com/fifa-world-ranking" /></>}
        />
        <FaqListItem
          question="What do I get if I win?"
          answer="Bragging rights! If you organize a pool you may establish your own prizes, but this website does not
          support sports betting and will not hand out any rewards or prizes."
        />
        <FaqListItem
          question="Can I edit my bracket before the tournament?"
          answer="Yes, an account is required to edit your bracket."
        />
        <FaqListItem
          question="Is there a way to delete a bracket?"
          answer={'Brackets can be deleted only by the pool owner. Just click the "Manage pool" button on your ' +
            'pool\'s leaderboard, or contact your pool\'s owner for assistance. Brackets created in the public pool ' +
            'cannot be deleted.'}
        />
        <FaqListItem
          question="Is there a way to delete a pool?"
          answer="Not at this time, but any pools that do not have any brackets after the tournament will be
          automatically deleted."
        />
        <FaqListItem
          question="General feedback, questions, or technical support?"
          answer={<>Send us an email at <MailSupport />. We look forward to hearing from you!</>}
        />
        <FaqListItem
          question="Credits"
          answer={<>
            <UnsplashCredit
              photo="Home"
              href="https://unsplash.com/@farzadme?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
              text="Farzad Mohsenvand"
            />
            <br />
            <UnsplashCredit
              photo="Trophy leaderboard"
              href="https://unsplash.com/es/@fznsr_?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
              text="Fauzan Saari"
            />
            <br />
            <UnsplashCredit
              photo="Created"
              href={"https://unsplash.com/@ninjason?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"}
              text="Jason Leung"
            />
            <br />
            <UnsplashCredit
              photo="Sydney leaderboard"
              href="https://unsplash.com/@liampozz?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
              text="Liam Pozz"
            />
            <br />
            <ExternalLink
              href='https://www.freepik.com/vectors/abstract-futuristic'
              text="Abstract futuristic vector created by BiZkettE1 - www.freepik.com"
            />
            <br />
            <ExternalLink
              href="https://www.freepik.com/free-vector/watercolor-blue-abstract-background_18776384.htm#page=3&query=pastels%20green%20and%20black&position=3&from_view=search&track=ais"
              text="Image by pikisuperstar on Freepik"
            />
          </>}
        />
      </ol>
    </div>
  )
}
